import { Database, Cloud, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useDataMode, ScreenName, DataMode } from '@/contexts/DataModeContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { toast } from 'sonner';

interface DataModeToggleProps {
  screen: ScreenName;
  className?: string;
  showLabel?: boolean;
}

export function DataModeToggle({ screen, className, showLabel = true }: DataModeToggleProps) {
  const { getScreenMode, setScreenMode } = useDataMode();
  const { refreshData } = useScheduler();
  const currentMode = getScreenMode(screen);
  const [isLoading, setIsLoading] = useState(false);

  const handleModeChange = async (mode: DataMode) => {
    if (mode !== currentMode) {
      if (mode === 'live') {
        setIsLoading(true);
        try {
          await refreshData();
          setScreenMode(screen, mode);
          toast.success('Live data loaded', {
            description: 'Fetched latest data from the database via API',
          });
        } catch (error) {
          toast.error('Failed to fetch live data', {
            description: 'Could not connect to the API. Please try again.',
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        setScreenMode(screen, mode);
        toast.info('Switched to Demo data', {
          description: 'Using sample data for demonstration',
        });
      }
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {showLabel && (
        <span className="text-sm font-medium text-muted-foreground">Data:</span>
      )}
      <div className="inline-flex rounded-lg border bg-muted p-1">
        <button
          type="button"
          onClick={() => handleModeChange('demo')}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all',
            currentMode === 'demo'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Database className="h-4 w-4" />
          Demo
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('live')}
          disabled={isLoading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all',
            currentMode === 'live'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
            isLoading && 'opacity-70 cursor-not-allowed'
          )}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Cloud className="h-4 w-4" />
          )}
          {isLoading ? 'Loading...' : 'Live'}
        </button>
      </div>
    </div>
  );
}
